import { z } from '@hono/zod-openapi';
import { usernameSchema } from './user';
import { TransactionSchema } from './transactions';

export const CustomerForAnalyticsSchema = z.object({ created_at: z.string(), notifications_allowed: z.boolean() });
export type CustomerForAnalytics = z.infer<typeof CustomerForAnalyticsSchema>;

export const CustomerMetadataSchema = z
  .record(z.string())
  .openapi({
    description: `
A flexible object for storing **custom, top-level metadata** related to the customer.

Only flat key-value pairs are allowed. Each value must be a:
- String

Nested objects, arrays, numbers, booleans, and null are **not allowed**.

### Example
\`\`\`json
{
  "loyalty_level": "gold",
  "referral_code": "X123YZ",
  "notes": "VIP customer"
}
\`\`\`
    `.trim()
  });

export type CustomerMetadata = z.infer<typeof CustomerMetadataSchema>

export const customerSchema = z.object({
  id: z.string().uuid(),
  display_name: z.string().optional(),
  username: usernameSchema,
  points: z.coerce.number(),
  notifications_allowed: z.boolean(),
  added_at: z.string(),
  metadata: CustomerMetadataSchema,
  birthdate: z.string().date().optional(),
});

export type Customer = z.infer<typeof customerSchema>

export const customersGroupTypeSchema = z.enum(['manual', 'automatic']);
export type CustomersGroupType = z.infer<typeof customersGroupTypeSchema>

//Operators
export const numberOperator = z.enum(['less_than', 'more_than', 'equal', 'not_equal']);
export type NumberOperator = z.infer<typeof numberOperator>
export const dateOperator = z.enum(['before', 'after', 'equal', 'not_equal']);
export type DateOperator = z.infer<typeof dateOperator>
export const stringOperator = z.enum(['equal', 'not_equal']);
export type StringOperator = z.infer<typeof stringOperator>
export const enumOperator = z.enum(['equal', 'not_equal']);
export type EnumOperator = z.infer<typeof enumOperator>

const addedTimeFilter = z.object({
  property: z.literal('added_time'),
  operator_type: z.literal('date'),
  operator: dateOperator,
  value: z.string().datetime()
});

const addedDaysAgoFilter = z.object({
  property: z.literal('added_days_ago'),
  operator_type: z.literal('number'),
  operator: numberOperator,
  value: z.number()
});

const ageFilter = z.object({
  property: z.literal('age'),
  operator_type: z.literal('number'),
  operator: numberOperator,
  value: z.number()
});

const pointsFilter = z.object({
  property: z.literal('points'),
  operator_type: z.literal('number'),
  operator: numberOperator,
  value: z.number()
});

const totalPurchaseAmountFilter = z.object({
  property: z.literal('total_purchase_amount'),
  operator_type: z.literal('number'),
  operator: numberOperator,
  value: z.number()
});

const latestVisitTimeFilter = z.object({
  property: z.literal('latest_visit_time'),
  operator_type: z.literal('date'),
  operator: dateOperator,
  value: z.string().datetime()
});

const latestVisitDaysFilter = z.object({
  property: z.literal('latest_visit_days_ago'),
  operator_type: z.literal('number'),
  operator: numberOperator,
  value: z.number()
});

const latestVisitSalesPointFilter = z.object({
  property: z.literal('latest_visit_sales_point'),
  operator_type: z.literal('enum'),
  operator: enumOperator,
  value: z.string()
});

const addedSalesPointFilter = z.object({
  property: z.literal('added_sales_point'),
  operator_type: z.literal('enum'),
  operator: enumOperator,
  value: z.string()
});

const customMetadataFilter = z.object({
  property: z.literal('metadata'),
  metadata_key: z.string().min(1),
  operator_type: z.literal('string'),
  operator: stringOperator,
  value: z.string()
});

export const customersFilterSchema = z.discriminatedUnion('property', [
  addedTimeFilter,
  addedDaysAgoFilter,
  latestVisitDaysFilter,
  ageFilter,
  pointsFilter,
  totalPurchaseAmountFilter,
  latestVisitTimeFilter,
  latestVisitSalesPointFilter,
  addedSalesPointFilter,
  customMetadataFilter
]);

export type CustomersFilter = z.infer<typeof customersFilterSchema>;

export type CustomersFilterProperty = z.infer<typeof customersFilterSchema>['property']
export type CustomersFilterOperator = z.infer<typeof customersFilterSchema>['operator']
export type CustomersFilterOperatorType = z.infer<typeof customersFilterSchema>['operator_type']

export const customersFilterPropertiesOptions: {
  label: string;
  value: CustomersFilterProperty;
  type: CustomersFilterOperatorType;
}[] = [
  // Group: Anagrafica
  { label: 'Età', value: 'age', type: 'number' },

  // Group: Attività nel tempo
  { label: 'Data di aggiunta', value: 'added_time', type: 'date' },
  { label: 'Numero di giorni dall’aggiunta', value: 'added_days_ago', type: 'number' },

  { label: 'Ultima visita', value: 'latest_visit_time', type: 'date' },
  { label: 'Numero di giorni dall\'ultima visita', value: 'latest_visit_days_ago', type: 'number' },

  // Group: Comportamento d'acquisto
  { label: 'Punti', value: 'points', type: 'number' },
  { label: 'Importo totale acquisti', value: 'total_purchase_amount', type: 'number' },

  // Group: Punto vendita
  { label: 'Punto vendita di aggiunta', value: 'added_sales_point', type: 'enum' },
  { label: 'Punto vendita ultima visita', value: 'latest_visit_sales_point', type: 'enum' }
];


export const createCustomersGroupSchema = z.object({
  name: z.string().optional(),
  type: z.literal('manual'),
  rules: customersFilterSchema.array().nullish()
}).or(
  z.object({
    name: z.string().optional(),
    type: z.literal('automatic'),
    rules: customersFilterSchema.array()
  })
);

export const customersGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string().optional(),
  type: z.literal('manual'),
  shop_id: z.string().uuid(),
  rules: customersFilterSchema.array().nullish(),
  created_at: z.string()
}).or(
  z.object({
    id: z.string().uuid(),
    name: z.string().optional(),
    type: z.literal('automatic'),
    rules: customersFilterSchema.array(),
    shop_id: z.string().uuid(),
    created_at: z.string()
  })
);

export type CustomersGroup = z.infer<typeof customersGroupSchema>

export const historyRecordSchema = TransactionSchema.extend({ record_type: z.literal('transaction') });

export type CustomerHistoryRecord = z.infer<typeof historyRecordSchema>

export const customerPaginatedHistorySchema = z.object({
  records: historyRecordSchema.array(),
  hasMore: z.boolean(),
  nextPage: z.number().nullable()
})

export type CustomerPaginatedHistory = z.infer<typeof customerPaginatedHistorySchema>
